/* eslint-disable react/require-default-props */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { UserStatus } from 'module/api/user/types';
import { ChevronDown, Close, TrashCan } from '@carbon/icons-react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { List, Space } from 'antd';
import classNames from 'classnames';
import { debounce } from 'lodash';
import React, { FC, forwardRef, useCallback, useRef } from 'react';
import ReactSelect, {
  Props,
  components as RsComponents,
  GroupedOptionsType,
  createFilter,
  defaultTheme,
  OnChangeValue,
} from 'react-select';
import AsyncReactSelect, { AsyncProps } from 'react-select/async';
import ReactCreatableSelect, { CreatableProps } from 'react-select/creatable';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Color, Short01, SingleLineEllipsis, Text01 } from '../../styles';
import { Avatar } from '../avatar';
import {
  BackgroundColor,
  HeightStyle,
  InputBorder,
  InputFocus,
} from '../input/input';
import { Tag as TagAtom, TagProps } from '../tag';
import {
  IndicatorSeparator,
  Input,
  StyledDropdownIndicator,
} from './components';
import { AvatarOption } from './custom-components';
import { OptionTypeBase, SelectMode } from './types';
import { FontAwesomeIcon } from '../icon/icon';

export const selectPrefix = 'ozone-select';

interface ClassNameOnlyProps {
  className?: string;
}
type UserTagProps = TagProps & {
  imageUrl?: string;
  label?: string;
  companyLogoUrl?: string;
  hoverColor?: string;
  userStatus?: UserStatus;
  hideAvatar?: boolean;
};

export enum InputChangeActions {
  InputBlur = 'input-blur',
  MenuClose = 'menu-close',
}

const Tag = styled(TagAtom)`
  padding: 0 var(--spacing-02) 0 0;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyleAvatar = styled(Avatar)`
  margin-right: var(--spacing-03);
  flex-shrink: 0;

  &.disabled {
    opacity: 0.5;
  }
`;

const MultiValueTagWrapper = styled.span`
  margin-left: var(--spacing-01);
  margin-right: var(--spacing-01);
`;

const Ellipsis = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// TODO: move this out
export const UserTag: FC<UserTagProps> = ({
  color = 'gray', // not sure if we need this default in code @Shajansheriff
  imageUrl,
  label,
  companyLogoUrl,
  icon,
  userStatus,
  hideAvatar,
  ...tagProps
}) => {
  return (
    <Tag {...tagProps} color={color} data-cy={`tag-value-${label}`}>
      <Wrapper>
        {!hideAvatar && (
          <StyleAvatar
            size="small"
            companyLogoUrl={companyLogoUrl}
            /**
             * if icon is passed, name and src is unset
             * since avatar component renders icon only when name/ src is unset
             */
            {...(icon ? { icon } : { name: label, src: imageUrl })}
            className={userStatus === UserStatus.Inactive ? 'disabled' : ''}
            userStatus={userStatus}
          />
        )}
        <Ellipsis title={label}>{label}</Ellipsis>
      </Wrapper>
    </Tag>
  );
};
const iconSize = {
  small: 16,
  middle: 20,
  large: 20,
};

export const DropdownIndicator = (props) => {
  return (
    <StyledDropdownIndicator {...props}>
      <ChevronDown size={iconSize[props.selectProps.size]} />
    </StyledDropdownIndicator>
  );
};

export const StyledControl = styled(RsComponents.Control).attrs({
  theme: {
    ...defaultTheme,
    borderRadius: 0,
  },
})`
  ${InputBorder};
  ${BackgroundColor};
  ${Short01};
  ${Text01};
  min-height: ${HeightStyle};

  .ozone-select__value-container {
    max-height: calc(3 * ${HeightStyle});
  }
  .ozone-select__placeholder {
    position: absolute;
    margin-left: var(--spacing-02);
    padding-left: var(--spacing-01);
  }
  &.ozone-select__control--menu-is-open {
    .ozone-select__single-value {
      color: var(--text-placeholder);
      opacity: 0.6;
    }
  }
  .ozone-select__single-value {
    padding-left: var(--spacing-02);
  }

  ${ifProp(
    'isFocused',
    css`
      ${InputFocus};
    `,
  )};
  ${ifProp(
    'isDisabled',
    css`
      ${BackgroundColor};
      color: var(--text-disabled);
      /* padding: 2px; */
    `,
  )};
  &:hover {
    border-color: var(--field-border-hover);
  }
`;

export const StyledClear = styled(RsComponents.ClearIndicator).attrs({
  theme: {
    ...defaultTheme,
  },
})`
  padding: 0 var(--spacing-03);
  &:hover {
    color: var(--gray-100);
    background-color: var(--black-with-opacity);
    border-radius: var(--border-radius-default);
    cursor: pointer;
  }
`;
export const Control = (props) => (
  <StyledControl size={props.selectProps.size} {...props} />
);

export const ClearIndicator = (props) => (
  <StyledClear {...props}>
    <FontAwesomeIcon icon={faXmark} size={iconSize[props.selectProps.size]} />
  </StyledClear>
);

const ListItem = styled(List.Item)`
  padding: 0;
`;

const ListItemMeta = styled(List.Item.Meta)`
  align-items: center;

  & .ant-list-item-meta-title {
    margin-bottom: 0;
    color: var(--gray-100);
  }

  & .ant-list-item-meta-description {
    font-size: var(--font-size-sm);
    color: var(--gray-80);
    font-weight: normal;
  }
`;

const StyledOption = styled(RsComponents.Option).attrs({
  theme: {
    ...defaultTheme,
    borderRadius: 0,
  },
})``;

const Option = (props) => {
  const { data } = props;

  return (
    <StyledOption {...props}>
      <ListItem>
        <ListItemMeta
          avatar={
            <Avatar
              name={data.__isNew__ ? data.value : data.label}
              src={data.imageUrl}
              userStatus={data?.userStatus}
            />
          }
          title={data.label}
          description={data.description}
        />
      </ListItem>
    </StyledOption>
  );
};

const StyledMultiValue = styled(RsComponents.MultiValue).attrs({
  theme: {
    ...defaultTheme,
    borderRadius: 0,
  },
})`
  --tag-border-color: var(--tag-surface-color);

  border-radius: var(--border-radius-default);
  height: var(--height-sm);
  margin: var(--spacing-01) 0;
  padding: 0;
  border: 2px solid var(--tag-border-color);
  background: var(--tag-surface-color);
  color: var(--tag-color);
  font-size: var(--font-size-sm);
  line-height: var(--spacing-05);
  display: inline-flex;
  align-items: center;
  overflow: hidden;

  .ozone-select__multi-value__label {
    border-radius: 0px;
  }

  &:focus {
    --tag-border-color: var(--blue-60);
  }

  &.disabled,
  &[disabled] {
    --tag-surface-color: var(--gray-10);
    --tag-color: var(--gray-30);
  }

  ${({ color }) => css`
    --tag-surface-color: var(--ozone-${color}-surface);
    --tag-color: var(--ozone-${color});
  `};

  cursor: ${ifProp('onClick', 'pointer', 'initial')};
`;

const StyledSingleValue = styled(RsComponents.SingleValue).attrs({
  theme: {
    ...defaultTheme,
    borderRadius: 0,
  },
})``;

export const SingleUserValue = ({ data, ...props }) => {
  return (
    <span data-cy="select.field.options">
      <StyledSingleValue {...props}>
        <UserTag
          imageUrl={data.imageUrl}
          color={data.color}
          userStatus={data?.userStatus}
          label={data.__isNew__ ? data.value : (data.tagLabel ?? data.label)}
          hideAvatar={data.hideAvatar}
        />
      </StyledSingleValue>
    </span>
  );
};

export const MultiValue = (props) => {
  const { data } = props;

  return (
    <MultiValueTagWrapper data-cy={`tag-multi-value-${data.label}`}>
      <StyledMultiValue color={data?.color || 'gray'} {...props} />
    </MultiValueTagWrapper>
  );
};

const StyledMultiValueLabel = styled(RsComponents.MultiValueLabel).attrs({
  theme: {
    ...defaultTheme,
    borderRadius: 0,
  },
})``;

const MultiValueLabel = (props) => {
  const { data } = props;

  return (
    <StyledMultiValueLabel {...props}>
      <Space size="small">
        {!data?.hideAvatar && (
          <Avatar
            size="small"
            src={data.imageUrl}
            name={data.__isNew__ ? data.value : data.label}
            userStatus={data?.userStatus}
          />
        )}
        <div>{data.tagLabel ?? data.label}</div>
      </Space>
    </StyledMultiValueLabel>
  );
};

const StyledMultiValueRemove = styled(RsComponents.MultiValueRemove).attrs({
  theme: {
    ...defaultTheme,
    borderRadius: 0,
  },
})``;

const MultiValueRemove = ({ data, ...props }) => {
  const isDisabled = Boolean(
    props?.selectProps?.isDisabled || data?.isOptionDisabled,
  );

  return isDisabled ? null : (
    <StyledMultiValueRemove {...props}>
      <Close size={16} />
    </StyledMultiValueRemove>
  );
};

const StyledTag = styled(TagAtom)`
  ${SingleLineEllipsis};
  display: unset;
`;

export const StyledRSOption = styled(RsComponents.Option).attrs({
  theme: {
    ...defaultTheme,
    borderRadius: 0,
  },
})`
  display: flex;
  min-width: 0;
`;
export const TagOption = (props) => {
  const { data } = props;

  return (
    <StyledRSOption {...props}>
      <StyledTag
        title={data.label}
        color={data.color}
        data-cy={`tag-option-${data.label}`}
      >
        {data.label}
      </StyledTag>
    </StyledRSOption>
  );
};

const StyledOptionWithSelector = styled(RsComponents.Option).attrs({
  theme: {
    ...defaultTheme,
    spacing: defaultTheme.spacing,
  },
})``;

const OptionWithSelector = (props) => {
  const { data } = props;

  return (
    <StyledOptionWithSelector {...props}>
      <span data-cy={`select-option-${data.label}`}>{props.children}</span>
    </StyledOptionWithSelector>
  );
};

const SingleValueRSComponent = styled(RsComponents.SingleValue).attrs({
  theme: {
    ...defaultTheme,
  },
})``;

export const TagSingleValue = (props) => {
  const { data } = props;

  return (
    <SingleValueRSComponent {...props}>
      <TagAtom color={data.color} data-cy={`tag-single-value-${data.label}`}>
        {data?.deleted ? <TrashCan size={16} /> : null}
        {data.label}
      </TagAtom>
    </SingleValueRSComponent>
  );
};

interface Option {
  label?: string;
  value?: string | number;
  count?: Color;
  description?: string;
  imageUrl?: string;
  isLoading?: boolean;
}

interface BaseSelectProps {
  type?: SelectMode;
  options?: Option[];
}

interface CustomSelectProps extends Props {
  closeMenuOnSelect?: boolean;
}

type SelectOptions<OptionsType, IsMulti extends boolean> = ReadonlyArray<
  IsMulti extends true ? GroupedOptionsType<OptionsType> : OptionsType
>;

type BaseProps<OptionsType, IsMulti> = {
  type?: SelectMode;
  options?: SelectOptions<OptionsType, IsMulti>;
  value?: OnChangeValue<OptionsType, IsMulti>;
};

export type CreatableSelectProps = BaseSelectProps &
  Omit<CreatableProps<Option, boolean>, 'options'> &
  Omit<CustomSelectProps, 'options'>;

export type AsyncSelectProps = BaseSelectProps &
  Omit<AsyncProps<Option, boolean>, 'options'> &
  Omit<CustomSelectProps, 'options'>;

export const CreatableSelect: FC<CreatableSelectProps & ClassNameOnlyProps> = ({
  className,
  components = {},
  ...props
}) => {
  const { type, showMenuOnOpen } = props;

  let customComponents: any = {
    DropdownIndicator: !showMenuOnOpen && (() => null),
    IndicatorSeparator: !showMenuOnOpen && (() => null),
    ClearIndicator,
    Control,
    MultiValueRemove,
  };

  if (type === 'user') {
    customComponents = {
      ...customComponents,
      Option: AvatarOption,
      SingleValue: SingleUserValue,
      MultiValue,
      MultiValueLabel,
    };
  } else if (type === 'tag') {
    customComponents = {
      ...customComponents,
      Option: TagOption,
      SingleValue: TagSingleValue,
      MultiValue,
    };
  }

  return (
    <ReactCreatableSelect
      className={classNames(`${selectPrefix}-container`, className)}
      classNamePrefix={selectPrefix}
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        colors: {
          ...theme.colors,
          primary: 'var(--primary-color)',
        },
      })}
      components={{
        ...customComponents,
        ...components,
      }}
      {...props}
    />
  );
};

export type SelectProps<
  OptionType = OptionTypeBase,
  IsMulti extends boolean = false,
> = BaseProps<OptionType, IsMulti> &
  Omit<CustomSelectProps<OptionType, IsMulti>, 'options' | 'value'>;

const useFilterOptions = (value) => {
  return useCallback(
    (option, rawInput) => {
      const inputIsNotEmpty = rawInput?.trim?.()?.length > 0;
      const isSelected = !!value?.some?.((val) => val?.value === option?.value);

      const searchFilter = option?.label
        ?.toLowerCase()
        ?.includes?.(rawInput?.toLowerCase());

      if (option?.data?.userStatus === UserStatus.Inactive) {
        return (
          (isSelected && searchFilter) || (inputIsNotEmpty && searchFilter)
        );
      }

      return searchFilter;
    },
    [value],
  );
};

export const Select = forwardRef<any, SelectProps>(
  ({ className, components = {}, ...props }, ref) => {
    const { value, type = 'basic' } = props;

    const filterOptions = useFilterOptions(value);

    let customComponents: any = {
      DropdownIndicator,
      ClearIndicator,
      Control,
      MultiValueRemove,
      Option: OptionWithSelector,
      Input: Input,
      IndicatorSeparator: IndicatorSeparator,
    };

    if (type === 'user') {
      customComponents = {
        ...customComponents,
        Option: AvatarOption,
        SingleValue: SingleUserValue,
        MultiValue,
        MultiValueLabel,
      };
    } else if (type === 'tag') {
      customComponents = {
        ...customComponents,
        Option: TagOption,
        SingleValue: TagSingleValue,
        MultiValue,
      };
    }

    /* TODO:: Remove this duplicate and make it into single. Note: I am stupidly sleepy while writing this component. So just POC implementation. */
    return (
      <ReactSelect
        ref={ref}
        className={classNames('ozone-select-container', className)}
        classNamePrefix="ozone-select"
        theme={(theme) => ({
          ...theme,
          borderRadius: 0,
          colors: {
            ...theme.colors,
            primary: 'var(--primary-color)',
          },
        })}
        components={{
          ...customComponents,
          ...components,
        }}
        {...props}
        filterOption={
          type === 'user'
            ? (option, rawInput) => {
                return filterOptions(option, rawInput);
              }
            : createFilter({
                matchFrom: 'any',
                stringify: (option) => `${option.label}`,
              })
        }
      />
    );
  },
);

Select.displayName = 'Select';

export const AsyncSelect: FC<AsyncSelectProps> = forwardRef<
  any,
  AsyncSelectProps
>(({ className, components = {}, ...props }, ref) => {
  const { value, type = 'basic' } = props;

  const filterOptions = useFilterOptions(value);

  let customComponents: any = {
    DropdownIndicator,
    ClearIndicator,
    Control,
    MultiValueRemove,
    Option: OptionWithSelector,
  };

  if (type === 'user') {
    customComponents = {
      ...customComponents,
      Option: AvatarOption,
      SingleValue: SingleUserValue,
      MultiValue,
      MultiValueLabel,
    };
  } else if (type === 'tag') {
    customComponents = {
      ...customComponents,
      Option: TagOption,
      SingleValue: TagSingleValue,
      MultiValue,
    };
  }

  // Create ref to manage debounced function
  // We need to use a ref because we need to mutate the function,
  // and we dont want to create a new function on every render.
  const debouncedLoadRef = useRef(
    debounce((input: string, callback: (options: any[]) => void) => {
      props.loadOptions(input).then(callback);
    }, 300),
  );

  // with the ref in place, we can call the function and get the latest version of the function
  // without creating a new function on every render.
  const loadOptions = (input: string) =>
    new Promise((resolve) => debouncedLoadRef.current(input, resolve));

  /* TODO:: Remove this duplicate and make it into single. Note: I am stupidly sleepy while writing this component. So just POC implementation. */
  return (
    <AsyncReactSelect
      ref={ref}
      className={classNames('ozone-select-container', className)}
      classNamePrefix="ozone-select"
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        colors: {
          ...theme.colors,
          primary: 'var(--primary-color)',
        },
      })}
      components={{
        ...customComponents,
        ...components,
      }}
      {...props}
      loadOptions={loadOptions}
      filterOption={
        type === 'user'
          ? (option, rawInput) => {
              return filterOptions(option, rawInput);
            }
          : createFilter({
              matchFrom: 'any',
              stringify: (option) => `${option.label}`,
            })
      }
    />
  );
});

AsyncSelect.displayName = 'AsyncSelect';

export const UserComponents = {
  Option: AvatarOption,
  SingleValue: SingleUserValue,
  MultiValue,
  MultiValueLabel,
};

export const TagComponents = {
  Option: TagOption,
  SingleValue: TagSingleValue,
  MultiValue,
};
